import React from "react";
import * as Yup from "yup";
import { useFormik, getIn, type FormikErrors } from "formik";
import { UIInput } from "components/Inputfields";
import { showSuccess, showError } from "toast/toast";
import {
    uploadCloudFile,
    getCloudFileById,
    resolveImageId,
    hardDeletePresignedFiles,
} from "components/Imageshelper";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { left } from "@popperjs/core";
import { getAuthCookie } from "../../utils/authCookies";
const token = getAuthCookie("sessionToken") || "";
// console.log(token, "token in template form");
const API_BASE = `${process.env.REACT_APP_API_URL}/invoice-templates`;

type SocialLinksPayload = {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
    youtube?: string;
};

type TemplatePayload = {
    template_name: string;
    images?: { url?: string | null };
    company_name?: string;
    invoice_prefix?: string;
    address?: string;
    phone_number?: string;
    email?: string;
    website?: string;
    custom_message?: string;
    default_template?: boolean;
    social_links?: SocialLinksPayload;
};

// replace with your real brokerage uploader if different
const isUrl = (v?: string) => !!v && (/^(https?:)?\/\//i.test(v) || /^data:/i.test(v));

/** Resolve a persisted id or url to a displayable URL */
async function resolvePreviewUrl(idOrUrl?: string): Promise<string> {
    if (!idOrUrl) return "";
    // try {
    //     // Many backends return ids. resolveImageId should return a final S3 URL when given an id.
    //     const u1 = await resolveImageId(idOrUrl, token);
    //     if (u1 && typeof u1 === "string") return u1;
    // } catch { }
    // // Fallbacks
    // if (isUrl(idOrUrl)) return idOrUrl;
    try {
        const fileObj = await getCloudFileById(idOrUrl, token);
        console.log(fileObj, "fileObj");
        const u2 = fileObj?.url || fileObj?.url || "";
        return typeof u2 === "string" ? u2 : "";
    } catch { }
    return "";
}

/** Extract file id from upload result (handles different shapes) */
function pickUploadedId(res: any): string {
    if (!res) return "";
    if (typeof res === "string") return res;
    return res.id || res.file_id || res._id || res.key || "";
}

/** Invoice prefix: A–Z only, max 3 characters, stored uppercase. */
function normalizeInvoicePrefix(raw?: string): string {
    return String(raw || "")
        .toUpperCase()
        .replace(/[^A-Z]/g, "")
        .slice(0, 3);
}

const emptySocialLinks = (): SocialLinksPayload => ({
    facebook: "",
    instagram: "",
    twitter: "",
    linkedin: "",
    youtube: "",
});

/** Build API payload for social_links (trimmed; omit empty strings). */
function socialLinksPayloadFromValues(sl?: SocialLinksPayload): SocialLinksPayload | undefined {
    if (!sl) return undefined;
    const out: SocialLinksPayload = {
        facebook: sl.facebook?.trim() || undefined,
        instagram: sl.instagram?.trim() || undefined,
        twitter: sl.twitter?.trim() || undefined,
        linkedin: sl.linkedin?.trim() || undefined,
        youtube: sl.youtube?.trim() || undefined,
    };
    if (!Object.values(out).some(Boolean)) return undefined;
    return out;
}

/** Same look as UIInput; supports nested Formik paths (e.g. social_links.facebook). */
function SocialLinkUIInput({
    formik,
    name,
    label,
    placeholder,
}: {
    formik: ReturnType<typeof useFormik<TemplatePayload>>;
    name: string;
    label: React.ReactNode;
    placeholder?: string;
}) {
    const [hover, setHover] = React.useState(false);
    const [focus, setFocus] = React.useState(false);

    const touched = getIn(formik.touched, name);
    const error = getIn(formik.errors, name);
    const hasError = !!(touched && error);

    const bgColor = hover || focus ? "#fafafa" : "#f5f6fa";
    const borderColor = hasError ? "#747981" : hover || focus ? "#9ca3af" : "#c9ced6";

    const style: React.CSSProperties = {
        width: "100%",
        height: 39,
        borderRadius: 10,
        padding: "10px 12px",
        borderWidth: 2,
        borderStyle: "solid",
        borderColor,
        backgroundColor: bgColor,
        outline: "none",
        transition: "all .2s ease-in-out",
        boxShadow: "none",
        color: "#111827",
    };

    const inputId = name.replace(/\./g, "-");

    return (
        <div className="mb-3">
            <label className="form-label" htmlFor={inputId}>
                {label}
            </label>
            <input
                id={inputId}
                name={name}
                className={`form-control ${hasError ? "is-invalid" : ""}`}
                style={style}
                value={getIn(formik.values, name) ?? ""}
                onChange={formik.handleChange}
                onFocus={() => setFocus(true)}
                onBlur={(e) => {
                    setFocus(false);
                    formik.handleBlur(e);
                }}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                placeholder={placeholder}
                type="text"
                aria-invalid={hasError}
            />
            {hasError ? <div className="invalid-feedback d-block">{String(error)}</div> : null}
        </div>
    );
}


export default function InvoiceTemplateForm() {

    const { templateId } = useParams<{ templateId: string }>();
    const isEdit = Boolean(templateId);
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [logoUrl, setLogoUrl] = React.useState<string>("");
    const [previewUrl, setPreviewUrl] = React.useState<string>(""); // what we show in UI
    const [newFile, setNewFile] = React.useState<File | null>(null); // newly selected file (not uploaded yet)
    const [persistedId, setPersistedId] = React.useState<string>(""); // existing stored id (from server)
    const [deleteAfterSaveId, setDeleteAfterSaveId] = React.useState<string>(""); // mark deletion
    /** Ref so Formik `validate` always sees latest logo UI state (preview/blob/file/id). */
    const logoFieldStateRef = React.useRef({
        newFile: null as File | null,
        persistedId: "",
        previewUrl: "",
    });
    React.useEffect(() => {
        logoFieldStateRef.current = { newFile, persistedId, previewUrl };
    }, [newFile, persistedId, previewUrl]);
    // keep initial values in state so Formik can reinitialize when we load edit data
    const [initialValues, setInitialValues] = React.useState<TemplatePayload>({
        template_name: "",
        images: { url: "" },
        company_name: "",
        invoice_prefix: "INV",
        address: "",
        phone_number: "",
        email: "",
        website: "",
        custom_message: "",
        default_template: false,
        social_links: emptySocialLinks(),
    });

    // load on edit
    React.useEffect(() => {
        if (!templateId) return;
        (async () => {
            try {
                setLoading(true);
                const res = await fetch(`${API_BASE}/${templateId}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!res.ok) throw new Error("Failed to fetch template");
                const data = await res.json();

                const stored = data?.images?.url ?? "";
                const display = await resolvePreviewUrl(stored);
                console.log(display, "loaded template data");
                setInitialValues({
                    template_name: data?.template_name ?? "",
                    images: { url: stored || "" },
                    company_name: data?.company_name ?? "",
                    invoice_prefix: normalizeInvoicePrefix(data?.invoice_prefix) || "INV",
                    address: data?.address ?? "",
                    phone_number: data?.phone_number ?? "",
                    email: data?.email ?? "",
                    website: data?.website ?? "",
                    custom_message: data?.custom_message ?? "",
                    default_template: Boolean(data?.default_template),
                    // social_links: {
                    //     ...emptySocialLinks(),
                    //     ...(data?.social_links && typeof data.social_links === "object" ? data.social_links : {}),
                    // },
                });
                setPreviewUrl(display);
                // Keep persisted id only if not a full URL
                const pid = !isUrl(stored) ? String(stored) : "";
                setPersistedId(pid);
                setNewFile(null);
                setDeleteAfterSaveId("");
                logoFieldStateRef.current = {
                    newFile: null,
                    persistedId: pid,
                    previewUrl: display,
                };
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, [templateId]);
    const TemplateSchema = Yup.object({
        invoice_prefix: Yup.string()
            .transform((value) => normalizeInvoicePrefix(value))
            .required("Invoice prefix is required")
            .matches(/^[A-Z]{3}$/, "Enter exactly 3 uppercase letters"),
        // company_name: Yup.string().trim().required("Company name is required"),
        // email: Yup.string().trim().email("Enter a valid email").required("Email is required"),
        // Optional:
        // website: Yup.string().url("Enter a valid URL").nullable().optional(),
        // address, phone_number, custom_message are optional in this example
    });

    // === FORMIK ===
    const formik = useFormik<TemplatePayload>({
        initialValues,
        enableReinitialize: true,
        validationSchema: TemplateSchema,
        validate: (values) => {
            const errors = {} as FormikErrors<TemplatePayload> & { company_logo?: string };
            const { newFile: nf, persistedId: pid, previewUrl: pv } = logoFieldStateRef.current;
            const hasLogo =
                Boolean(nf) ||
                Boolean(pid?.trim()) ||
                Boolean(values.images?.url?.trim()) ||
                Boolean(pv?.trim());
            if (!hasLogo) {
                errors.company_logo = "Company logo is required.";
            }
            const prefix = normalizeInvoicePrefix(values.invoice_prefix);
            if (prefix.length !== 3) {
                errors.invoice_prefix = "Enter exactly 3 uppercase letters";
            }
            return errors;
        },
        onSubmit: async (values, helpers) => {
            const errs = await helpers.validateForm();
            if (Object.keys(errs).length > 0) {
                helpers.setTouched(
                    Object.keys(errs).reduce((acc, k) => ({ ...acc, [k]: true }), {} as any),
                    true
                );
                showError("Please fix the required fields before saving.");
                return;
            }

            try {
                setSaving(true);
                // 1) Handle upload if the user picked a new file
                let imageIdToSave: string | undefined = values.images?.url || undefined;

                if (newFile) {
                    // ✅ pass a single options object
                    console.log(newFile, token, "newFile");
                    const uploaded = await uploadCloudFile({ file: newFile, token: token });
                    const newId = uploaded?.id;            // uploadCloudFile returns a CloudFile
                    imageIdToSave = newId || undefined;
                } else if (!previewUrl && persistedId) {
                    imageIdToSave = undefined;             // clear on server
                }

                // 2) Build payload
                const social_links = socialLinksPayloadFromValues(values.social_links);
                const payload: TemplatePayload = {
                    ...values,
                    invoice_prefix: normalizeInvoicePrefix(values.invoice_prefix),
                    images: imageIdToSave ? { url: imageIdToSave } : undefined, // send id (or url) only when present
                    default_template: Boolean(values.default_template),
                };

                // console.log(payload, "payload");

                const res = await fetch(isEdit ? `${API_BASE}/${templateId}` : API_BASE, {
                    method: isEdit ? "PATCH" : "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify(payload),
                });
                if (!res.ok) throw new Error("Save failed");
                if (deleteAfterSaveId) {
                    try {
                        await hardDeletePresignedFiles([deleteAfterSaveId], token);
                    } catch (e) {
                        // non-blocking; log and continue
                        console.warn("Failed to delete old file:", e);
                    }
                    setDeleteAfterSaveId("");
                }

                showSuccess(templateId ? "Template updated successfully!" : "Template added successfully!");
                navigate(-1);
            } catch (err) {
                console.error(err);
                alert("Unable to save template.");
            } finally {
                setSaving(false);
            }
        },
    });

    // file choose/upload
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);
    const onChooseFile = () => fileInputRef.current?.click();

    const onFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const f = e.target.files?.[0] || null;
        if (!f) return;

        // Revoke previous object URL (if any)
        if (previewUrl && previewUrl.startsWith("blob:")) {
            URL.revokeObjectURL(previewUrl);
        }

        // show instant preview
        const blobUrl = URL.createObjectURL(f);
        let nextPersisted = persistedId;
        if (persistedId) {
            setDeleteAfterSaveId(persistedId);
            setPersistedId("");
            nextPersisted = "";
        }
        logoFieldStateRef.current = {
            newFile: f,
            persistedId: nextPersisted,
            previewUrl: blobUrl,
        };
        setPreviewUrl(blobUrl);
        setNewFile(f);

        // clear form value; it will be set to the new id after upload on submit
        formik.setFieldValue("images", { url: "" });

        // allow re-selecting the same file
        e.currentTarget.value = "";
    };

    const removeLogo = () => {
        // If we had a blob preview, revoke it
        if (previewUrl && previewUrl.startsWith("blob:")) {
            URL.revokeObjectURL(previewUrl);
        }

        setPreviewUrl("");
        setNewFile(null);

        // If there was a stored id (edit case), mark it for deletion after save
        if (persistedId) {
            setDeleteAfterSaveId(persistedId);
            setPersistedId("");
        }

        logoFieldStateRef.current = { newFile: null, persistedId: "", previewUrl: "" };

        // Clear form field
        formik.setFieldValue("images", { url: "" });
    };
    return (
        <div className="container my-4">
            <div className="col-md-9">
                {/* Header */}
                <div className="d-flex align-items-start gap-2 mb-3">
                    <div
                        className="rounded-3 d-inline-flex align-items-center justify-content-center"
                        style={{ width: 36, height: 36, background: "#EAF1FF", color: "#1E66FF" }}
                    >
                        <i className="bi bi-file-earmark-text" />
                    </div>
                    <div>
                        <h4 className="mb-0">Orders/Invoice Templates</h4>
                        <div className="text-muted">Configure your company information for invoices</div>
                    </div>
                    {/* <div className="ms-auto">
          {onClose && (
            <button className="btn btn-outline-secondary" onClick={onClose}>
              Close
            </button>
          )}
        </div> */}
                </div>

                {/* Company Logo */}
                <div className="card shadow-lg  mb-4 template-card">
                    <div className="card-body">
                        <h6 className="fw-semibold d-flex align-items-center gap-2 mb-3">
                            <i className="bi bi-upload" /> Company Logo <span className="text-danger">*</span>
                        </h6>

                        {!previewUrl ? (
                            <div className="dropzone d-flex flex-column align-items-center justify-content-center text-center">
                                <div className="display-6 text-secondary mb-2">
                                    <i className="bi bi-cloud-arrow-up" />
                                </div>
                                <div className="fw-semibold">Upload your logo</div>
                                <div className="text-muted small">PNG, JPG, or SVG up to 5MB</div>

                                <button type="button" className="btn btn-light border mt-3" onClick={onChooseFile}>
                                    <i className="bi bi-upload me-2" />
                                    Choose File
                                </button>
                            </div>
                        ) : (
                            <div className="logo-preview position-relative">
                                <img src={previewUrl} alt="logo" className="rounded w-100 h-100 object-fit-contain" />
                                <button
                                    type="button"
                                    className="btn btn-sm rounded-circle position-absolute  translate-middle"
                                    style={{ left: "163px", top: "10px" }}
                                    onClick={removeLogo}
                                    title="Remove"
                                >
                                    <i className="bi bi-x-lg" />
                                </button>
                            </div>
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml"
                            className="d-none"
                            onChange={onFileChange}
                        />
                        {(formik.errors as { company_logo?: string }).company_logo ? (
                            <div className="text-danger small mt-2" role="alert">
                                {(formik.errors as { company_logo?: string }).company_logo}
                            </div>
                        ) : null}
                    </div>
                </div>


                {/* Company Information */}
                <div className="">
                    <div className="">
                        {/* <h6 className="fw-semibold mb-3">
                        <i className="bi bi-buildings me-2" />
                        Company Information
                    </h6> */}

                        <form onSubmit={formik.handleSubmit}>
                            <div className="card shadow-lg mb-4 template-card">
                                <div className="card-body">
                                    <h6 className="fw-semibold mb-3">
                                        <i className="bi bi-chat-right-text me-2" />
                                        Custom Message
                                    </h6>
                                    <div className="row g-3">
                                        {/* <div className="col-12">
                                <UIInput
                                    formik={formik}
                                    name="template_name"
                                    label="Template Name *"
                                    placeholder="Standard Template"
                                />
                            </div> */}

                                        <div className="col-md-6">
                                            <UIInput
                                                formik={formik}
                                                name="company_name"
                                                label="Company Name"
                                                // required
                                                placeholder="Enter your company name"
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <UIInput
                                                formik={{
                                                    ...formik,
                                                    handleChange: ((e: React.ChangeEvent<HTMLInputElement>) => {
                                                        formik.setFieldValue(
                                                            "invoice_prefix",
                                                            normalizeInvoicePrefix(e.target.value)
                                                        );
                                                    }) as typeof formik.handleChange,
                                                }}
                                                name="invoice_prefix"
                                                label="Invoice Prefix"
                                                required
                                                placeholder="INV"
                                                autoCapitalize="characters"
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Address</label>
                                            <textarea
                                                id="address"
                                                name="address"
                                                className="form-control always-border"
                                                rows={4}
                                                placeholder="Enter your complete business address"
                                                value={formik.values.address || ""}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                        </div>
                                        <div className="col-md-6"></div>

                                        <div className="col-12 col-lg-6">
                                            <UIInput
                                                formik={formik}
                                                name="phone_number"
                                                label={
                                                    <>
                                                        <i className="bi bi-telephone me-2" /> Phone Number
                                                    </>
                                                }
                                                placeholder="(555) 123-4567"
                                            />
                                        </div>
                                        <div className="col-12 col-lg-6">
                                            <UIInput
                                                formik={formik}
                                                name="email"
                                                // required
                                                label={
                                                    <>
                                                        <i className="bi bi-envelope me-2" /> Email Address
                                                    </>
                                                }
                                                placeholder="contact@company.com"
                                            />
                                        </div>

                                        <div className="col-12">
                                            <UIInput
                                                formik={formik}
                                                name="website"
                                                label={
                                                    <>
                                                        <i className="bi bi-globe me-2" /> Website
                                                    </>
                                                }
                                                placeholder="https://www.company.com"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Custom Message */}
                            <div className="card shadow-lg mt-4 mb-4 template-card">
                                <div className="card-body">
                                    <h6 className="fw-semibold mb-3">
                                        <i className="bi bi-chat-right-text me-2" />
                                        Custom Message
                                    </h6>
                                    <label className="form-label">Invoice Message</label>
                                    <textarea
                                        id="custom_message"
                                        name="custom_message"
                                        className="form-control always-border"
                                        rows={5}
                                        placeholder="Add a custom message that will appear on your invoices (e.g., payment terms, thank you note, etc.)"
                                        value={formik.values.custom_message || ""}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <div className="text-muted small mt-2">
                                        This message will appear at the bottom of all your invoices
                                    </div>
                                </div>
                            </div>

                            {/* Default Template */}
                            <div className="card shadow-lg mb-4 template-card">
                                <div className="card-body">
                                    <h6 className="fw-semibold mb-3">
                                        <i className="bi bi-bag-check me-2" />
                                        Template Settings
                                    </h6>

                                    <div className="form-check mt-3">
                                        <input
                                            id="makeDefault"
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={Boolean(formik.values.default_template)}
                                            onChange={(e) => {
                                                formik.setFieldValue("default_template", Boolean(e.target.checked));
                                            }}
                                        />
                                        <label htmlFor="makeDefault" className="form-check-label">
                                            Make this as default template
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Social Links */}
                            {/* <div className="card shadow-lg mb-4 template-card">
                                <div className="card-body">
                                    <h6 className="fw-semibold mb-3">
                                        <i className="bi bi-share me-2" />
                                        Social Links
                                    </h6>
                                    <div className="row g-3">
                                        <div className="col-12 col-lg-6">
                                            <SocialLinkUIInput
                                                formik={formik}
                                                name="social_links.facebook"
                                                label={
                                                    <>
                                                        <i className="bi bi-facebook me-2" /> Facebook
                                                    </>
                                                }
                                                placeholder="https://facebook.com/yourprofile"
                                            />
                                        </div>
                                        <div className="col-12 col-lg-6">
                                            <SocialLinkUIInput
                                                formik={formik}
                                                name="social_links.instagram"
                                                label={
                                                    <>
                                                        <i className="bi bi-instagram me-2" /> Instagram
                                                    </>
                                                }
                                                placeholder="https://instagram.com/yourprofile"
                                            />
                                        </div>
                                        <div className="col-12 col-lg-6">
                                            <SocialLinkUIInput
                                                formik={formik}
                                                name="social_links.twitter"
                                                label={
                                                    <>
                                                        <i className="bi bi-twitter-x me-2" /> Twitter / X
                                                    </>
                                                }
                                                placeholder="https://x.com/yourprofile"
                                            />
                                        </div>
                                        <div className="col-12 col-lg-6">
                                            <SocialLinkUIInput
                                                formik={formik}
                                                name="social_links.linkedin"
                                                label={
                                                    <>
                                                        <i className="bi bi-linkedin me-2" /> LinkedIn
                                                    </>
                                                }
                                                placeholder="https://linkedin.com/in/yourprofile"
                                            />
                                        </div>
                                        <div className="col-12 col-lg-6">
                                            <SocialLinkUIInput
                                                formik={formik}
                                                name="social_links.youtube"
                                                label={
                                                    <>
                                                        <i className="bi bi-youtube me-2" /> YouTube
                                                    </>
                                                }
                                                placeholder="https://youtube.com/@yourchannel"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div> */}

                            <div className="d-flex justify-content-start gap-2">
                                <button
                                    className="btn btn-primary save-btn surface-btn fw-bold fs-16 px-4 py-2"
                                    type="submit"
                                    disabled={saving}
                                    style={{ background: "#1E66FF", borderColor: "#1E66FF", fontWeight: 600 }}
                                >
                                    <i className="bi bi-save me-2" />
                                    {isEdit ? "Update Invoice Template" : "Save Invoice Template"}
                                </button>
                                {/* 
              {onClose && (
                <button className="btn btn-light border" type="button" onClick={onClose}>
                  Cancel
                </button>
              )} */}
                            </div>
                        </form>
                    </div>
                </div>

                {/* styles to match your screenshots */}
                <style>{`
        .template-card { border-radius: 19px; }
        .dropzone {
          height: 220px;
          border: 2px dashed #cfd6e4;
          border-radius: 16px;
          background: #fafcff;
        }
        .logo-preview {
          width: 180px;
          height: 130px;
          border-radius: 12px;
          background: #f8fafc;
          border: 1px solid #e6e9ef;
          overflow: hidden;
        }
        .always-border, .always-border:focus {
          border: 2px solid #cfd6e4 !important;
          box-shadow: none !important;
          background: #f7f8fc;
        }
      `}</style>
            </div>
        </div>
    );
}
